import { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { Download, Copy } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Slider } from '@/components/ui/Slider';
import { copyToClipboard, downloadCanvas } from '@/lib/utils';
import { toast } from 'sonner';

const PRESETS = [
  { id: 'url', label: 'URL', sample: 'https://utility.os' },
  { id: 'email', label: 'Email', sample: 'mailto:hi@utility.os?subject=Hello' },
  { id: 'phone', label: 'Phone', sample: 'tel:+919999999999' },
  { id: 'wifi', label: 'Wi-Fi', sample: 'WIFI:T:WPA;S:Office;P:s3cret;;' },
  { id: 'sms', label: 'SMS', sample: 'sms:+919999999999?body=Hello' },
  { id: 'text', label: 'Text', sample: 'Utility OS — your work, simplified.' },
];

export function QrGenerator() {
  const [text, setText] = useState('https://utility.os');
  const [size, setSize] = useState(280);
  const [margin, setMargin] = useState(2);
  const [fg, setFg] = useState('#0a0a0b');
  const [bg, setBg] = useState('#ffffff');
  const [ec, setEc] = useState<'L' | 'M' | 'Q' | 'H'>('M');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !text) return;
    QRCode.toCanvas(canvasRef.current, text, {
      width: size,
      margin,
      color: { dark: fg, light: bg },
      errorCorrectionLevel: ec,
    }).catch(() => {
      // ignored — invalid input
    });
  }, [text, size, margin, fg, bg, ec]);

  return (
    <div>
      <ToolHeader
        toolId="qr-generator"
        title="QR code generator"
        description="Generate styled QR codes for URLs, Wi-Fi, SMS, or any text — with size, margin, color, and error-correction options."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <Card>
            <Field label="Content">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter URL, text, Wi-Fi credentials…"
              />
            </Field>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setText(p.sample)}
                  className="px-2.5 py-1 text-2xs rounded-md border border-border hover:border-accent hover:bg-accent/5 transition"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </Card>

          <Card className="space-y-4">
            <Field label="Size" hint={`${size}px`}>
              <Slider
                value={size}
                onChange={setSize}
                min={120}
                max={680}
                step={20}
                showValue={false}
              />
            </Field>
            <Field label="Margin" hint={`${margin} modules`}>
              <Slider value={margin} onChange={setMargin} min={0} max={8} showValue={false} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Foreground">
                <input
                  type="color"
                  value={fg}
                  onChange={(e) => setFg(e.target.value)}
                />
              </Field>
              <Field label="Background">
                <input
                  type="color"
                  value={bg}
                  onChange={(e) => setBg(e.target.value)}
                />
              </Field>
              <Field label="Error correction">
                <Select
                  value={ec}
                  onChange={(e) => setEc(e.target.value as typeof ec)}
                  options={[
                    { value: 'L', label: 'L · 7%' },
                    { value: 'M', label: 'M · 15%' },
                    { value: 'Q', label: 'Q · 25%' },
                    { value: 'H', label: 'H · 30%' },
                  ]}
                />
              </Field>
            </div>
          </Card>
        </div>

        <Card className="lg:sticky lg:top-4 self-start flex flex-col items-center justify-center">
          <div className="rounded-xl bg-bg-inset p-4 mb-4 inline-flex">
            <canvas ref={canvasRef} className="rounded-md" />
          </div>
          <div className="w-full grid grid-cols-2 gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (canvasRef.current) downloadCanvas(canvasRef.current, 'qrcode.png');
              }}
            >
              <Download size={12} />
              PNG
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                if (!canvasRef.current) return;
                const url = canvasRef.current.toDataURL();
                const ok = await copyToClipboard(url);
                if (ok) toast.success('Data URL copied');
              }}
            >
              <Copy size={12} />
              Data URL
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default QrGenerator;
